import { z } from 'zod';
// Artefacts de jobs (`artifacts:`) des pipelines d'un commit — GU-378, tranche 1.
//
// `status` ne se déduit jamais de la seule date d'expiration : sur l'instance, une date passée peut
// coexister avec une archive encore téléchargeable (règle « keep latest » de GitLab, hypothèse non
// vérifiée) comme avec une archive supprimée. La règle retenue vit dans api/services/ci.ts.
export const CiJobArtifactStatus = z.enum(['available', 'deleted']);
export const CiJobArtifactDTO = z.object({
    jobId: z.number(),
    jobName: z.string(),
    jobUrl: z.string(),
    status: CiJobArtifactStatus,
    // Taille de l'archive ; null quand elle a disparu.
    size: z.number().nullable(),
    // `artifacts_expire_at` brut ; null = pas d'expiration. Informatif seulement, voir plus haut.
    expireAt: z.string().nullable(),
});
// Défini ici car `CiPipelineDTO` le porte ; voir « Jobs manuels et configuration CI » plus bas.
export const CiManualJobDTO = z.object({
    id: z.number(),
    name: z.string(),
    stage: z.string(),
    // Page du job dans GitLab : « Ouvrir dans GitLab », et lien d'erreur quand le lancement échoue.
    webUrl: z.string(),
    pipelineId: z.number(),
    // true si le stage est dans la liste blanche du dépôt : propose « Lancer ». Ne dit rien du droit de
    // l'utilisateur à le lancer, c'est GitLab qui tranche (403).
    playable: z.boolean(),
});
export const CiPipelineDTO = z.object({
    id: z.number(),
    // Brut : nom de branche, nom de tag, ou `refs/merge-requests/<iid>/head` pour un pipeline de MR.
    ref: z.string(),
    source: z.string().nullable(),
    status: z.string(),
    webUrl: z.string(),
    createdAt: z.string(),
    artifacts: z.array(CiJobArtifactDTO),
    // Tous les jobs au statut `manual` du pipeline, lançables ou non (`playable`) : les autres ne sont
    // pas masqués, la tooltip en donne le nombre avec un lien vers GitLab.
    manualJobs: z.array(CiManualJobDTO),
});
// Packages du Package Registry — GU-378, tranche 2. Rattachés au commit par `pipeline.id` du package
// (champ absent du type gitbeaker, présent dans le JSON brut) : un package sans ce champ (anciens
// packages, publication hors CI) n'est pas rattaché, donc pas affiché.
export const CiPackageDTO = z.object({
    id: z.number(),
    name: z.string(),
    version: z.string(),
    packageType: z.string(),
    // Pipeline du commit auquel GitLab rattache le package.
    pipelineId: z.number(),
    // Lien absolu vers la page du package dans GitLab ; null si GitLab ne l'a pas fourni.
    webUrl: z.string().nullable(),
});
// Images du Container Registry GitLab — GU-378, tranche 2. Rattachées au commit par le tag d'image :
// égal au sha court du commit (`sha`) ou au nom d'un tag git qu'il porte (`tag`). Un registre externe
// à GitLab n'est pas visible ici.
export const CiImageDTO = z.object({
    // Chemin du dépôt d'images dans le registre.
    repository: z.string(),
    tag: z.string(),
    // Référence complète `registre/dépôt:tag`, utilisable dans `docker pull`.
    location: z.string(),
    matchedBy: z.enum(['sha', 'tag']),
    // `total_size` du tag selon GitLab (octets, couches compressées dans le registre : ce que l'on
    // télécharge, pas la taille décompressée). null si GitLab ne le fournit pas : jamais 0 par défaut.
    size: z.number().nullable(),
});
export const CommitCiDTO = z.object({
    sha: z.string(),
    // Les pipelines les plus récents du sha en premier, dans la limite d'un plafond (voir
    // `pipelinesTruncated`) ; chacun porte ses artefacts, éventuellement aucun.
    pipelines: z.array(CiPipelineDTO),
    // true si le sha a plus de pipelines que le plafond : les plus anciens ne sont pas interrogés.
    pipelinesTruncated: z.boolean(),
    packages: z.array(CiPackageDTO),
    // true si l'index des packages du dépôt dépasse le plafond de pages : `packages` est alors vide
    // plutôt que potentiellement faux (un package du commit pourrait se trouver au-delà).
    packagesTruncated: z.boolean(),
    images: z.array(CiImageDTO),
    // Liste blanche de stages appliquée à `manualJobs[].playable` (config du dépôt, ou défaut). Renvoyée
    // pour que la tooltip l'affiche et propose « Configurer ».
    allowedStages: z.array(z.string()),
});
// Jobs manuels et configuration CI — GU-378, tranche 3 (écriture sur GitLab).
//
// Un job est manuel quand GitLab lui donne le statut `manual` : le statut du pipeline lui-même reste
// `success` malgré ces jobs en attente, il faut lister les jobs. Seuls ceux dont le `stage` figure
// dans la liste blanche du dépôt sont lançables ; les autres sont comptés et renvoyés vers GitLab.
export const DEFAULT_CI_ALLOWED_STAGES = ['publish', 'package', 'image'];
// Nom de stage tel que saisi dans la modal « Configuration CI » (séparés par des virgules : une
// virgule dans un nom ne pourrait jamais être saisie).
const CiStageName = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .refine((s) => !s.includes(','), 'a stage name cannot contain a comma');
// Stockée par dépôt (`Repository.ciConfig`), validée à la lecture comme `branchColorRules`. Une liste
// vide est refusée : elle désactiverait silencieusement tous les boutons « Lancer ». Pour revenir au
// défaut, on supprime la config (`ciConfig` = null), pas une liste vide.
export const CiConfig = z.object({
    allowedStages: z.array(CiStageName).min(1).max(20),
});
// Corps du `PUT /repos/:id/ci-config` : `allowedStages: null` supprime la config du dépôt (retour au
// défaut, bouton « Réinitialiser »). Une liste vide reste refusée, voir `CiConfig`.
export const CiConfigInput = z.object({ allowedStages: CiConfig.shape.allowedStages.nullable() });
// Résultat d'un `play` réussi : le job passe de `manual` à `pending` ou `created`.
export const PlayJobResponse = z.object({
    jobId: z.number(),
    status: z.string(),
    webUrl: z.string(),
});
// Corps d'erreur d'un `play` refusé : jamais un échec muet. `jobUrl` permet d'ouvrir le job dans GitLab
// (artefacts de dépendance supprimés, variable ou input exigé au lancement, etc.).
export const PlayJobError = z.object({
    error: z.string(),
    jobUrl: z.string().nullable(),
});
// Commits ayant produit un package — GU-380. Lecture seule, un seul appel sur l'index des packages du
// dépôt, joint sur `pipeline.sha`. La liste dit « présent » uniquement : un sha absent ne veut JAMAIS
// dire « sans package » (package sans pipeline, index tronqué, sha hors du graphe, pipeline de MR
// « merge result » dont le sha est temporaire).
export const PackageCommitsResponse = z.object({
    // Shas complets (minuscules) portant au moins un package, dédoublonnés.
    shas: z.array(z.string()),
    // true si l'index dépasse le plafond de pages : des marqueurs peuvent manquer (la liste reste exacte
    // pour ce qu'elle contient, contrairement à `packagesTruncated` qui vide la liste d'un commit).
    truncated: z.boolean(),
});
//# sourceMappingURL=ci.js.map